import React from 'react';
import { differenceInDays, parseISO } from 'date-fns';
import { Sprout, Scale, Droplets, Zap, Sparkles, Award, TrendingUp, SunMedium } from 'lucide-react';
import { Job, Entry } from '../types';

interface AnalyticsViewProps {
  job: Job;
  entries: Entry[];
}

export const AnalyticsView: React.FC<AnalyticsViewProps> = ({ job, entries }) => {
  const sproutDate = job.settings.sproutDate ? parseISO(job.settings.sproutDate) : new Date(job.created_at * 1000);
  const daysActive = Math.max(1, differenceInDays(new Date(), sproutDate));

  // Yield calculations
  const yieldEntries = entries.filter((e) => e.category_type === 'yield');
  const totalYield = yieldEntries.reduce((sum, e) => {
    const val = Number(e.custom_fields?.yield_amount || e.custom_fields?.f_yield || 0);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  const targetYield = job.settings.targetYield || 500;
  const yieldProgress = Math.min(100, Math.round((totalYield / targetYield) * 100));

  // Category counts
  const waterings = entries.filter((e) => e.category_type === 'watering').length;
  const feedings = entries.filter((e) => e.category_type === 'feeding').length;
  const milestones = entries.filter((e) => e.category_type === 'milestone').length;

  // Temp & Humidity averages
  const temps: number[] = [];
  const humidities: number[] = [];
  entries.forEach((e) => {
    if (e.custom_fields?.f_temp) temps.push(Number(e.custom_fields.f_temp));
    if (e.custom_fields?.f_humidity) humidities.push(Number(e.custom_fields.f_humidity));
  });

  const avgTemp = temps.length > 0 ? (temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1) : null;
  const avgHum = humidities.length > 0 ? (humidities.reduce((a, b) => a + b, 0) / humidities.length).toFixed(1) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Top Highlights Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        <div className="glass-panel stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="stat-label">Plant Age / Stage</span>
            <Sprout size={18} color="var(--accent-emerald-light)" />
          </div>
          <div className="stat-value">{daysActive} Days</div>
          <div className="stat-sub">{job.settings.currentStage || 'Vegetative Stage'}</div>
        </div>

        <div className="glass-panel stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="stat-label">Total Harvested</span>
            <Scale size={18} color="var(--accent-emerald)" />
          </div>
          <div className="stat-value">
            {totalYield} {job.settings.yieldUnit || 'g'}
          </div>
          <div className="stat-sub">Target: {targetYield} {job.settings.yieldUnit || 'g'}</div>
        </div>

        <div className="glass-panel stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="stat-label">Light Schedule</span>
            <SunMedium size={18} color="var(--accent-amber)" />
          </div>
          <div className="stat-value">{job.settings.lightSchedule || '18/6'}</div>
          <div className="stat-sub">Photoperiod Cycle</div>
        </div>

        <div className="glass-panel stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="stat-label">Care Actions</span>
            <Droplets size={18} color="var(--accent-teal)" />
          </div>
          <div className="stat-value">{waterings + feedings}</div>
          <div className="stat-sub">{waterings} Waterings · {feedings} Feedings</div>
        </div>
      </div>

      {/* Yield Target Progress Bar */}
      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Award size={18} color="var(--accent-emerald)" />
              <span>Harvest Yield Progress</span>
            </h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Logged weight: {totalYield} / {targetYield} {job.settings.yieldUnit || 'g'}
            </p>
          </div>
          <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--accent-emerald-light)' }}>
            {yieldProgress}%
          </span>
        </div>

        <div
          style={{
            height: 12,
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: varRadius(6),
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${yieldProgress}%`,
              background: 'linear-gradient(90deg, #10b981, #34d399)',
              borderRadius: varRadius(6),
              transition: 'width 0.5s ease',
            }}
          />
        </div>
      </div>

      {/* Environment Trends */}
      {(avgTemp || avgHum) && (
        <div className="glass-panel" style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <TrendingUp size={18} color="var(--accent-teal)" />
            <span>Environmental Averages</span>
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
            {avgTemp && (
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '1rem', borderRadius: 'var(--radius-md)' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-sub)' }}>Avg Temperature</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#f59e0b' }}>{avgTemp} °F</div>
              </div>
            )}
            {avgHum && (
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '1rem', borderRadius: 'var(--radius-md)' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-sub)' }}>Avg Relative Humidity</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#38bdf8' }}>{avgHum} %</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function varRadius(n: number) {
  return `${n}px`;
}
